<template>
	<form class="flex flex-col h-fit sm:w-[600px] md:w-[745px] md:max-w-[745px] bg-neutral">
		<!-- Prevent implicit submission of the form -->
		<button type="submit" disabled style="display: none" aria-hidden="true"></button>

		<div v-if="breadcrumbs" class="flex flex-col">
			<div class="flex w-full space-x-3">
				<img src="@/assets/images/icon/arrow-left.svg" class="cursor-pointer" @click="cancel"/>
				<span v-html="breadcrumbs"/>
			</div>
			<span class="bg-[#B2BBC9] h-[1px] mt-4"/>
		</div>

		<div class="flex flex-col mt-4">
			<InputText class="flex-1" label="Name*" type="text" input-height="h-11" font-weight="font-normal" v-model="resource_name" />
			<InputText class="flex-1" label="Description" type="textarea" text-area-rows="2" input-height="h-11" font-weight="font-normal" v-model="resource_description" />
			<InputText class="flex-1" label="Url*" placeholder="https://www.google.com" type="text" input-height="h-11" font-weight="font-normal" v-model="resource_url" :errors="error" error-field="resource.url" />

			<div v-if="resource_groups != null && resource_groups.length > 0">
				<div class="flex">
					<span class="mr-1">Organization Groups</span>
					<Popper hover arrow content="This resource will only be visible to Organizations that belong to the selected Organization Groups below, or to all Organizations if none are selected.">
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" class="fill-current text-primary">
							<path d="M12 0.674988C5.73749 0.674988 0.674988 5.73749 0.674988 12C0.674988 18.2625 5.73749 23.3625 12 23.3625C18.2625 23.3625 23.3625 18.2625 23.3625 12C23.3625 5.73749 18.2625 0.674988 12 0.674988ZM12 21.675C6.67499 21.675 2.36249 17.325 2.36249 12C2.36249 6.67499 6.67499 2.36249 12 2.36249C17.325 2.36249 21.675 6.71249 21.675 12.0375C21.675 17.325 17.325 21.675 12 21.675Z" fill=""/>
							<path d="M12.675 4.53748C11.5875 4.34998 10.4625 4.64998 9.63748 5.36248C8.81248 6.07498 8.32498 7.08748 8.32498 8.21248C8.32498 8.62498 8.39998 9.03748 8.54998 9.44998C8.69998 9.89998 9.18748 10.125 9.63748 9.97498C10.0875 9.82498 10.3125 9.33748 10.1625 8.88748C10.0875 8.66248 10.05 8.43748 10.05 8.21248C10.05 7.61248 10.3125 7.04998 10.7625 6.67498C11.2125 6.29998 11.8125 6.11248 12.45 6.22498C13.2375 6.37498 13.9125 7.04998 14.0625 7.83748C14.2125 8.77498 13.725 9.71248 12.8625 10.0875C11.8875 10.5 11.25 11.475 11.25 12.6V14.925C11.25 15.375 11.625 15.7875 12.1125 15.7875C12.5625 15.7875 12.975 15.4125 12.975 14.925V12.6C12.975 12.1875 13.2 11.8125 13.5375 11.6625C15.15 10.9875 16.0875 9.29998 15.7875 7.57498C15.375 6.03748 14.175 4.79998 12.675 4.53748Z" fill=""/>
							<path d="M12.0375 17.175H11.925C11.475 17.175 11.0625 17.55 11.0625 18.0375C11.0625 18.525 11.4375 18.9 11.925 18.9H12.0375C12.4875 18.9 12.8625 18.525 12.8625 18.0375C12.8625 17.55 12.525 17.175 12.0375 17.175Z" fill=""/>
						</svg>
					</Popper>
				</div>
				<div class="flex mt-3 space-x-4">
					<CheckBox v-for="group in resource_groups" :key="group.id" :id="group.id" :label="group.name" ref="checkboxRefs" v-model="group.is_active" />
				</div>
			</div>
		</div>

		<div class="flex w-full mt-6">
			<div class="flex-grow">
				<SubmitButton v-if="resource_id" label="Delete" :type="ButtonType.Warning" :size="ButtonSize.Small"
											:disabled="isSaving || isDeleting" :loading="isDeleting" @click="onDeleteClicked" />
			</div>

			<SubmitButton label="Cancel" :type="ButtonType.Outline" :size="ButtonSize.Small" :disabled="isSaving || isDeleting" class="ml-auto mr-3" @click="cancel" />
			<SubmitButton label="Save" :size="ButtonSize.Small" :loading="isSaving" @click="saveClicked" :disabled="disableSaveButton" />
		</div>
	</form>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDiscardChangesModal"
			icon="alert"
			title="Discard Changes"
			message="Are you sure? Any unsaved changes will be lost."
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDiscardChangesModal = false }, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Yes', () => { dismiss() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>

	<Teleport to="html">
		<MultiButtonModal
			:show="showDeleteModal"
			icon="alert"
			title="Delete Resource"
			:message="`Are you sure?<br><br>This cannot be undone.`"
			:buttons="[
									new ModalButtonData(
										'Cancel', () => { showDeleteModal = false }, ButtonType.Default, ButtonSize.Small
									),
									new ModalButtonData(
										'Delete', () => { deleteResource() }, ButtonType.Default, ButtonSize.Small
									)
								]"
		/>
	</Teleport>
</template>

<script setup lang="js">
import InputText from "@/components/Forms/InputText.vue"
import { computed, onMounted, ref } from 'vue'
import SubmitButton from "@/components/Forms/Buttons/SubmitButton.vue"
import { ButtonSize, ButtonType } from "@/components/Forms/Buttons/SubmitButtonConstants.js"
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { resourcesApi } from '@/api/Organizations/Resources/ResourcesApi.js'
import CheckBox from '@/components/Forms/CheckBox.vue'
import { Toaster } from '@/models/utils/Toaster.js'
import Popper from 'vue3-popper'

const props = defineProps({
	show: { type: Boolean, default: false },
	resourceToEdit: { type: Object, default: null },
})

const isSaving = ref(false)
const isDeleting = ref(false)
const error = ref(null)
const resource_id = ref(null)
const breadcrumbs = ref(null)
const resource_name = ref(null)
const resource_description = ref(null)
const resource_url = ref(null)
const resource_groups = ref(null)
const showDiscardChangesModal = ref(false)
const showDeleteModal = ref(false)
const disableSaveButton = computed(() => {
	return isSaving.value || isDeleting.value || !resource_name.value || !resource_url.value
})
const checkboxRefs = ref([])
const toaster = new Toaster()
const emit = defineEmits(["dismiss", "resourceDeleted"])

onMounted(() => {
	handleResource(props.resourceToEdit)
})

function handleResource(resourceToEdit) {
	resource_id.value = resourceToEdit.id
	breadcrumbs.value = resourceToEdit.breadcrumbs
	resource_name.value = resourceToEdit.name
	resource_description.value = resourceToEdit.description
	resource_url.value = resourceToEdit.url

	if (resourceToEdit.groups) {
		resource_groups.value = JSON.parse(JSON.stringify(resourceToEdit.groups))
	}
}

function cancel() {
	if (changesMade()) {
		showDiscardChangesModal.value = true
	} else {
		dismiss()
	}
}

function changesMade() {
	const oldResourceName = props.resourceToEdit.name
	const oldResourceDescription = props.resourceToEdit.description
	const oldResourceUrl = props.resourceToEdit.url
	const oldResourceGroups = props.resourceToEdit.groups

	const groupsModified = oldResourceGroups?.some((oldGroup, index) => {
		return oldGroup.is_active !== checkboxRefs.value[index].getIsChecked()
	}) ?? false

	return (
		(resource_name.value != null && oldResourceName !== resource_name.value) || (resource_description.value != null && oldResourceDescription !== resource_description.value) ||
		(resource_url.value != null && oldResourceUrl !== resource_url.value) || groupsModified
	)
}

function saveClicked() {
	if (!changesMade()) {
		cancel()
		return
	}

	error.value = null
	isSaving.value = true

	const successCallback = (updatedResource) =>
	{
		isSaving.value = false
		dismiss(updatedResource['data'], false)
	}

	const errorCallback = (e) =>
	{
		isSaving.value = false
		error.value = e.showErrorBanner()
	}

	if (resource_id.value == null) {
		resourcesApi.createResource({
			'resource': {
				'subcategory_id': props.resourceToEdit.subcategory_id,
				'name': resource_name.value,
				'description': resource_description.value,
				'url': resource_url.value,
				'groups': resource_groups.value,
			}
		}, successCallback, errorCallback)
	}
	else {
		resourcesApi.updateResource({
			'resource': {
				'id': resource_id.value,
				'subcategory_id': props.resourceToEdit.subcategory_id,
				'name': resource_name.value,
				'description': resource_description.value,
				'url': resource_url.value,
				'groups': resource_groups.value,
			}
		}, successCallback, errorCallback)
	}
}

function onDeleteClicked() {
	showDeleteModal.value = true
}

function deleteResource() {
	error.value = null
	isDeleting.value = true

	resourcesApi.deleteResource(props.resourceToEdit.id,
		(success) => {
			error.value = null
			isDeleting.value = false

			if (success) {
				toaster.success('Resource successfully deleted!')
				dismiss(props.resourceToEdit, true)
			}	else {
				toaster.error('Resource failed to delete. Please try again.')
			}
		})
}

function dismiss(updatedResource, isDelete) {
	reset()

	if (isDelete) {
		emit('resourceDeleted', updatedResource)
	} else {
		emit('dismiss', updatedResource)
	}
}

function reset() {
	resource_id.value = null
	resource_name.value = null
	resource_description.value = null
	resource_url.value = null
	resource_groups.value = null
}

</script>